import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize } from '@lemon8/design';
import cls from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { $LandingModalOpen } from '~/models/portal/landing-modal';
import { ClickPosition } from '~/utils/click-position';
import { usePageActive } from '~/utils/active/use-page-active';
import useReflowClick from '~/utils/use-reflow-click';
import { FixBodyUtil } from '~/utils/fix-body';
import { setLocalStorageStatus } from 'shared/utils/local-storage-util';
import { $HitLandingModalMaskClickClose, $HitNewLandingModalButton } from '~/utils/libra/landing-modal-style';

import './style.scss';

export const LandingModalStyle: FC = () => {
  const [open, setOpen] = useAtom($LandingModalOpen);
  const active = usePageActive();
  const { t } = useTranslation();

  const hitLandingModalMaskClickClose = useAtomValue($HitLandingModalMaskClickClose);
  const hitLandingModalButton = useAtomValue($HitNewLandingModalButton);

  const handleNotNowClick = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_position: ClickPosition.first_landing_dialog,
        click_result: 'cancel',
      }),
      defaultBehavior: () => {
        setOpen(false);
      },
    },
    [],
  );

  const handleOpenAppClick = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_position: ClickPosition.first_landing_dialog,
        click_result: 'install',
      }),
      defaultBehavior: () => {
        active({
          schemaOption: {
            pageType: 'feed',
            categoryId: 0,
          },
          ampExtra: {
            enter_position: ClickPosition.first_landing_dialog,
          },
        });

        setOpen(false);
      },
    },
    [active],
  );

  const handleMaskClick = (): void => {
    if (hitLandingModalMaskClickClose) {
      setOpen(false);
    }

    window.Tea('wap_click', {
      current_position: ClickPosition.first_landing_dialog_mask,
    });
  };

  useEffect(() => {
    if (open) {
      window.Tea('wap_cta_show', {
        current_position: ClickPosition.first_landing_dialog,
      });
      const keyPrefix = 'seo_page_first_open_cta';
      const dateToday = ((): string => {
        const today = new Date();
        const date = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
        return date;
      })();
      FixBodyUtil.fix();
      setLocalStorageStatus(keyPrefix, `${keyPrefix}_${dateToday}`, '1');
    } else {
      FixBodyUtil.restore();
    }
  }, [open]);

  return (
    <div className={cls('seo-landing-modal-style-wrapper', { open, close: !open })}>
      <div className="seo-landing-modal-mask" onClick={handleMaskClick} />
      <div className={cls('seo-landing-modal-anchor')}>
        <div className={cls('seo-landing-modal')}>
          <div className="seo-landing-modal-logo" />
          <div className="seo-landing-modal-content">
            <div className="seo-landing-modal-title">{t('seo_landing_1st_modal_title')}</div>
            <div className="seo-landing-modal-text">{t('seo_landing_1st_modal_desc')}</div>
          </div>
          <Button
            className="seo-landing-modal-install-btn"
            size={ButtonSize.large}
            variant="Primary"
            title={t('sharing_popup_install')}
            onClick={handleOpenAppClick}
          />
          <Button
            className={cls('seo-landing-modal-cancel-btn', {
              strengthening: hitLandingModalButton,
            })}
            size={ButtonSize.large}
            variant={hitLandingModalButton ? 'Secondary' : 'Text'}
            title={t('sharing_popup_cancel')}
            onClick={handleNotNowClick}
          />
        </div>
      </div>
    </div>
  );
};
